/* file description */

import React, { FC, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { BetaSchemaForm, ProForm, ProFormSelect, ProFormText, ProFormInstance } from '@cui/pro-components';
import { Alert, Collapse, DatePicker, Space, ConfigProvider } from 'antd';
import type { SPFormWithModelProps } from './type';
import {
  useColumnsProp,
  useAddRecordForm,
  useRequestProp,
  useSaveForm,
  useModule,
  useDeleteForm,
  useValidateSubmitForm,
  useSendInitData,
  useOnValuesChange,
  useLayout,
  useLabelCol,
  useCollapse,
  useModelDefaultValues,
} from '../SPFormWithModel/effect';
import _, { uniqueId } from 'lodash';
import { useDeepCompareEffect, useDebounceFn } from 'ahooks';
import { Api as FetchAPI } from '@/utils';
import { SPForm, FormFrom, FormMode } from '../SPForm';
import styles from './index.module.less';

const { Panel } = Collapse;

export const SPGroupForm: FC<SPFormWithModelProps> = function SPGroupForm({
  moduleID,
  namespaceID,
  recordID = '0',
  recordColumns,
  mode,
  labelCol,
  formRef,
  layoutType,
  steps,
  onLoad,
  onValuesChange,
  forceFieldProps,
  initData,
  isOpenGroup,
  groupData,
  extral = {},
  assetModuleInfo,
  recordValues,
  value,
  storeValue,
  store,
  colProps,
  isMobile,
  ...otherProps
}) {
  const [key, setKey] = useState(uniqueId(recordID));
  const queryFormRef = useRef<any>();
  const { moduleInfo } = useModule({
    FetchAPI,
    namespaceID,
    moduleID,
    assetModuleInfo,
  });
  const { mergeColumns } = useColumnsProp({
    columns: recordColumns,
    moduleInfo,
    FetchAPI,
    mode,
    queryFormRef,
    isOpenGroup: true,
    storeValue,
    store,
    isMobile,
  });
  const { mergeRecordFormType, mergeRecordFormColumns } = useAddRecordForm({
    recordFormType: layoutType,
    columns: mergeColumns,
    steps,
  });

  const { mergeRecordColumnsList } = useCollapse({
    extral,
    isOpenGroup,
    RecordFormColumns: mergeRecordFormColumns,
    groupData,
  });

  const { modelDefaultValues } = useModelDefaultValues({
    recordID,
    mergeRecordFormColumns,
  });

  const { mergeRequest, formInitData } = useRequestProp({
    namespaceID,
    moduleID,
    FetchAPI,
    initData,
    moduleInfo,
    recordValues,
  });

  const { saveRequest } = useSaveForm({
    FetchAPI,
    namespaceID,
    moduleID,
    recordID,
    moduleInfo,
    formInitData,
  });
  const { deleteRequest } = useDeleteForm({
    FetchAPI,
    namespaceID,
    moduleID,
    recordID,
  });
  const { onSubmit } = useValidateSubmitForm({
    queryFormRef,
    saveRequest,
  });
  useSendInitData({
    formInitData,
    onLoad,
    // moduleInfo,
    // moduleID,
    // namespaceID,
  });
  const { mergeValuesChange } = useOnValuesChange({
    formInitData,
    onValuesChange,
  });

  const { layout } = useLayout({
    mode,
  });

  const { showLabelCol } = useLabelCol({
    mode,
    labelCol,
    isMobile,
  });

  const reload = () => {
    setKey(uniqueId());
  };

  // ref转发
  useImperativeHandle(formRef, () => ({
    queryForm: queryFormRef,
    onSubmit,
    onDelete: deleteRequest,
    reload,
    validateFields: () => queryFormRef.current?.validateFields(),
  }));

  const setData = (value) => {
    if (!queryFormRef.current) {
      setTimeout(() => {
        setData(value);
      }, 500);
    } else {
      queryFormRef.current?.setFieldsValue(value);
    }
  };

  // 外部数据变更时设置到表单上
  useEffect(() => {
    // 可能数据进来后，组件还未渲染
    setData(value);
  }, [value]);

  const { run: changeRun } = useDebounceFn(
    (...arg) => {
      mergeValuesChange(arg[0], queryFormRef.current?.getFieldsValue());
    },
    { wait: 400 },
  );

  if (moduleInfo.moduleID === '0') {
    return null;
  }

  let spformColumns: any = [];
  if (isOpenGroup) {
    mergeRecordColumnsList.forEach((mergeRecord) => {
      spformColumns.push([
        mergeRecord.title,
        _.isArray(mergeRecord.renderSchema) ?
          mergeRecord.renderSchema.map((x) => ({
            ...x,
            label: x.title,
            required: x.isRequired,
            name: x.dataIndex,
            fieldProps: x.fieldProps,
            formItemProps: x.formItemProps,
            render: x.render,
            renderFormItem: x.renderFormItem,
          }))
          : mergeRecord.renderSchema,
      ]);
    });
  } else {
    spformColumns = mergeRecordFormColumns.map((x) => ({
      ...x,
      label: x.title,
      required: x.isRequired,
      name: x.dataIndex,
      fieldProps: x.fieldProps,
      formItemProps: x.formItemProps,
      render: x.render,
      renderFormItem: x.renderFormItem,
    }));
  }

  return (
    <div className={`${isMobile ? styles['form-wrap-mobile'] : undefined}`}>
      <SPForm
        {...otherProps}
        mode={mode as FormMode}
        isOpenGroup={isOpenGroup}
        columns={spformColumns}
        from={FormFrom.Form}
        layout={otherProps?.layout ?? (mode === 'edit' ? 'vertical' : 'horizontal')}
        ref={queryFormRef}
        value={value}
        onValuesChange={(changeValue, allValue) => {
          changeRun(changeValue, allValue);
        }}
        colProps={isMobile ? {
          ...(colProps || {}),
          span: 24,
        } : colProps}
        request={mergeRequest}
        // eslint-disable-next-line no-nested-ternary
        params={{ recordID, modelDefaultValues: mode === 'read' ? {} : (value ? {} : modelDefaultValues) }}
        onFinish={saveRequest}
        isMobile={isMobile}
      />
      {
        isOpenGroup ? null : Object.values(extral).map((recordColumn: any, index) => {
          return <div key={index} style={{ width: '100%' }}> {recordColumn.renderSchema} </div>;
        })
      }
    </div>
  );
};

export default SPGroupForm;
